import { cn } from '@portals-platform/ui';
import { ChevronDown } from 'lucide-react';
import type * as React from 'react';
import { useController } from 'react-hook-form';
import { CONTROL_BASE, CONTROL_ERROR, CONTROL_INTERACTION } from '../lib/control-classes.js';
import { useFieldA11y } from '../lib/field-context.js';
import { useFieldError } from '../lib/use-field-error.js';

type SelectOption = string | { value: string; label: string };

interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  name: string;
  options: SelectOption[];
  placeholder?: string;
}

/**
 * Native `<select>` — right for short, known option lists. For long or searchable lists
 * use `ComboboxField`, and for multiple values `MultiSelectField`.
 *
 * Controlled by the form value, not registered as an uncontrolled input: a native select
 * discards a value whose `<option>` does not exist yet, and an uncontrolled one never gets it
 * back. Options that load after the form (an edit form, a country-dependent list) would stay
 * blank. Passing `value` lets React re-apply it whenever the options change.
 */
export function SelectField({
  name,
  options,
  placeholder,
  className,
  onChange,
  onBlur,
  ...props
}: SelectFieldProps) {
  const hasError = !!useFieldError(name);
  const a11y = useFieldA11y(name);
  const { field } = useController({ name });

  return (
    <div className="relative">
      <select
        {...a11y}
        className={cn(
          'vhf-select',
          CONTROL_BASE,
          CONTROL_INTERACTION,
          hasError && CONTROL_ERROR,
          className,
        )}
        {...props}
        name={field.name}
        ref={field.ref}
        value={field.value == null ? '' : String(field.value)}
        onChange={(event) => {
          field.onChange(event);
          onChange?.(event);
        }}
        onBlur={(event) => {
          field.onBlur();
          onBlur?.(event);
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => {
          const value = typeof opt === 'string' ? opt : opt.value;
          const label = typeof opt === 'string' ? opt : opt.label;
          return (
            <option key={value} value={value}>
              {label}
            </option>
          );
        })}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 size-[14px] -translate-y-1/2 text-muted-foreground"
      />
    </div>
  );
}
